import { useEffect, useRef } from 'react';

import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { isEqual } from 'lodash';

/** The slice of the grid api this hook reads. */
type GridSelectionWriter = Pick<GridApiPremium, 'setRowSelectionModel'>;

/**
 * Clears the row selection when the grouping changes.
 *
 * Every row id is built from the grouping path, so a regroup leaves the old
 * ticks pointing at rows that no longer exist. A grid with
 * `keepNonExistentRowsSelected` holds on to them, where the user can neither
 * see nor untick them, and they keep feeding the bulk actions.
 *
 * The previous model lives in a ref and is compared by value. React.Activity
 * re-runs this effect on every tab switch with the same grouping, and a
 * caller may hand back a fresh array with the same fields; neither is a
 * regroup, so neither clears what the user ticked.
 */
export function useClearSelectionOnRegroup({
  apiRef,
  rowGroupingModel,
}: {
  apiRef: React.RefObject<GridSelectionWriter | null>;
  rowGroupingModel: string[];
}): void {
  const previousModelRef = useRef(rowGroupingModel);

  useEffect(() => {
    if (isEqual(previousModelRef.current, rowGroupingModel)) return;
    previousModelRef.current = rowGroupingModel;
    apiRef.current?.setRowSelectionModel({ ids: new Set(), type: 'include' });
  }, [apiRef, rowGroupingModel]);
}
